import React, { useState, memo, useCallback, useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import SocialMediaIcons from '../../SocialMediaIcons/index';
import './AnglTokenBanner.css';
import AsSeenOnCarousel from "../../AsSeenOnCarousel/AsSeenOnCarousel"

const imgCDNUrl1 = process.env.REACT_APP_CDN_LINK;

// Uniswap Button Component
const UniswapButton = memo(({ buttonConfig }) => {
  const handleButtonClick = useCallback(() => {
    if (buttonConfig?.redirectUrl) {
      window.open(buttonConfig.redirectUrl, '_blank', 'noopener,noreferrer');
    }
  }, [buttonConfig]);

  if (!buttonConfig) return null;

  return (
    <div className="uniswap-button-section">
      <button 
        className="uniswap-button"
        onClick={handleButtonClick}
        aria-label={buttonConfig.buttonText}
      >
        <div className="uniswap-button-content">
          <div className="uniswap-logo-container">
            <img
              src={process.env.REACT_APP_CDN_LINK + buttonConfig.logoUrl}
              alt={buttonConfig.altText}
              className="uniswap-logo"
              loading="lazy"
            />
          </div>
          <span className="uniswap-button-text">
            {buttonConfig.buttonText}
          </span>
        </div>
        <div className="glossy-effect"></div>
      </button>
    </div>
  );
});

// Secondary Actions Component
const SecondaryActions = memo(({ actionsConfig }) => {
  if (!actionsConfig || !Array.isArray(actionsConfig)) return null;

  return (
    <div className="angl-token-secondary-actions">
      {actionsConfig.map((action, index) => (
        <a
          key={index}
          href={action.href}
          className="angl-token-action-link"
          target={action.href.startsWith('http') ? "_blank" : "_self"}
          rel={action.href.startsWith('http') ? "noopener noreferrer" : ""}
        >
          <span className="angl-token-action-primary">
            {action.primaryText}
          </span>
          {action.secondaryText && (
            <span className="angl-token-action-secondary">
              {action.secondaryText}
            </span>
          )}
        </a>
      ))}
    </div>
  );
});

// Token Image Component
const TokenImage = memo(({ imageConfig }) => {
  if (!imageConfig) return null;

  return (
    <div className="angel-token-image-container">
      <img
        src={imgCDNUrl1 + imageConfig.imagePath}
        alt={imageConfig.altText}
        className="angel-token-image"
        width={imageConfig.width}
        height={imageConfig.height}
        loading="lazy"
      />
    </div>
  );
});

// Updated FindUsOn Component with conditional auto-scroll
const FindUsOn = memo(() => {
  const socialLinks = [
    {
      url: "https://coinmarketcap.com/currencies/angel-twin/",
      icon: "myanglai/Images/coinmarketcap.webp",
      name: "CoinMarketCap"
    },
    {
      url: "https://www.coingecko.com/en/coins/angel-twin",
      icon: "myanglai/Images/coingecko.webp",
      name: "CoinGecko"
    },
    {
      url: "https://coinmarketcap.com/community/profile/ANGL_Token/",
      icon: "myanglai/Images/coinmarketcap.webp",
      name: "CoinMarketCap Community"
    },
    {
      url: "https://www.dextools.io/app/en/ether/pair-explorer/0xc1214937100e4efe56a9d5140ab5804b198b3a0a?t=1750254325482",
      icon: "myanglai/Images/dextools.webp",
      name: "DexTools"
    },
    {
      url: "https://coinmarketcap.com/community/profile/ANGL_Token/",
      icon: "myanglai/Images/coinmarketcap.webp",
      name: "CoinMarketCap Community"
    },
    {
      url: "https://www.dextools.io/app/en/ether/pair-explorer/0xc1214937100e4efe56a9d5140ab5804b198b3a0a?t=1750254325482",
      icon: "myanglai/Images/dextools.webp",
      name: "DexTools"
    },
    {
      url: "https://coinmarketcap.com/community/profile/ANGL_Token/",
      icon: "myanglai/Images/coinmarketcap.webp",
      name: "CoinMarketCap Community"
    },
    {
      url: "https://www.dextools.io/app/en/ether/pair-explorer/0xc1214937100e4efe56a9d5140ab5804b198b3a0a?t=1750254325482",
      icon: "myanglai/Images/dextools.webp",
      name: "DexTools"
    }
  ];

  // For desktop: if more than 4 logos, duplicate for auto-scroll, otherwise show as is
  // For mobile: always duplicate for infinite scroll
  const shouldAutoScrollDesktop = socialLinks.length > 4;
  const desktopLinks = shouldAutoScrollDesktop ? [...socialLinks, ...socialLinks] : socialLinks;
  const mobileLinks = [...socialLinks, ...socialLinks]; // Always duplicate for mobile

  return (
    <div className="find-us-on-section">
      <div className="find-us-on-container">
        <span className="find-us-on-text">Find Us On :</span>
        <div className={`social-logos-scroll-container ${shouldAutoScrollDesktop ? 'auto-scroll-desktop' : ''}`}>
          <div className="social-logos-wrapper">
            {/* Show different content for desktop vs mobile using CSS display */}
            <div className="desktop-logos">
              {desktopLinks.map((social, index) => (
                <a
                  key={`desktop-${social.name}-${index}`}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-logo-link"
                  aria-label={social.name}
                >
                  <img
                    src={imgCDNUrl1 + social.icon}
                    alt={social.name}
                    className="social-logo"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
            <div className="mobile-logos">
              {mobileLinks.map((social, index) => (
                <a
                  key={`mobile-${social.name}-${index}`}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-logo-link"
                  aria-label={social.name}
                >
                  <img
                    src={imgCDNUrl1 + social.icon}
                    alt={social.name}
                    className="social-logo"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const AnglTokenBanner = () => {
  const [bannerConfig, setBannerConfig] = useState(null);
  const [isInfoCardHovered, setIsInfoCardHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBannerConfig = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_CDN_LINK + "myanglai/jsons/angl-token.json");
        if (!response.ok) {
          throw new Error('Failed to fetch config');
        }
        const data = await response.json();
        setBannerConfig(data);
      } catch (error) {
        console.error('Error fetching banner config:', error);
        // Fallback configuration
        setBannerConfig({
          subtitle: "ANGL Token",
          title: {
            line1: "Own your AI.",
            line2: "Power it with ANGL"
          },
          uniswapButton: {
            buttonText: "Token Available on Uniswap",
            redirectUrl: "#",
            logoUrl: "angeltwin/uniswap-logo.webp",
            altText: "Uniswap Logo"
          },
          secondaryActions: [
            {
              href: "/contact",
              primaryText: "Join the AI Revolution",
              secondaryText: "Take Control Today"
            },
            {
              href: "https://whitepaper.myangl.ai/",
              primaryText: "Read the Whitepaper",
              secondaryText: ""
            }
          ],
          tokenImage: {
            imagePath: "myanglai/Images/angl-token-animation-3d.gif",
            altText: "ANGL Token",
            width: "400",
            height: "400"
          },
          infoCard: {
            title: "ANGL isn't just a token. It's the engine of a human-first AI economy—one where you own the tools, the data, and the rewards."
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBannerConfig();
  }, []);

  const handleInfoCardMouseEnter = useCallback(() => {
    setIsInfoCardHovered(true);
  }, []);

  const handleInfoCardMouseLeave = useCallback(() => {
    setIsInfoCardHovered(false);
  }, []);

  if (isLoading) {
    return <div className="angl-token-banner">Loading...</div>;
  }

  return (
    <div className="angl-token-banner">
      <Container>
        <Row className="angl-token-row">
          <Col xs={11} md={7} className="angl-token-left-column">
            <div className="angl-token-content">
              <span className="angl-token-subtitle">{bannerConfig?.subtitle}</span>

              <h1 className="angl-token-title">
                {bannerConfig?.title?.line1} <br />{bannerConfig?.title?.line2}
              </h1>

              <UniswapButton buttonConfig={bannerConfig?.uniswapButton} />

              <div className="angl-token-actions">
                <SecondaryActions actionsConfig={bannerConfig?.secondaryActions} />
              </div>

            </div>
          </Col>

          <Col xs={12} md={5} className="angl-token-right-column">
            <TokenImage imageConfig={bannerConfig?.tokenImage} />
          </Col>
        </Row>

        <Row className="angl-token-info-row">
          <Col>
            <div
              className="angl-token-info-container"
              onMouseEnter={handleInfoCardMouseEnter}
              onMouseLeave={handleInfoCardMouseLeave}
            >
              <div className={`angl-token-info-card ${isInfoCardHovered ? 'is-hovered' : ''}`}>
                <h2 className="angl-token-info-text">
                  {bannerConfig?.infoCard?.title}
                </h2>
              </div>
            </div>
          </Col>
        </Row>

        <div className="angl-mobile-social-icons">
          <SocialMediaIcons />
        </div>

        {/* New Find Us On section */}
        {/* <FindUsOn /> */}
        <AsSeenOnCarousel />
      </Container>
    </div>
  );
};

export default memo(AnglTokenBanner);